import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "What exactly is WYNN?",
    a: "A small, intelligent companion app for artists and makers. It blends AI self-coaching, daily affirmations, meditations and sound healing, mood journaling, gentle body practices, and a quiet community — all in one calm, pocket-sized space.",
  },
  {
    q: "When does the beta open?",
    a: "WYNN is in private build now, with the closed beta opening in 2026, iOS first. There's no countdown — we'll write you a simple letter when the doors quietly open. Waitlist members are invited first.",
  },
  {
    q: "What do beta testers get?",
    a: "Early access to the app, a direct line to shape the prompts, voice and rituals inside, founding-member pricing kept for life, and — if you want it — your name in the credits of v1.",
  },
  {
    q: "Is it free to join the waitlist?",
    a: "Completely free, and easy to leave any time. Joining simply places you in the first circle for an invite when the beta opens.",
  },
  {
    q: "Do the practices on this page do anything?",
    a: "Yes — the affirmation, the 5-minute breathing reset, and the mood check-in are real, working tastes of what lives inside WYNN. No sign-up needed. Take what you need and return to your work.",
  },
  {
    q: "Who is making WYNN?",
    a: "WYNN is built by hand by Maria-Katharina, the same maker behind the Awakened Artist Academy and the Born to Create podcast. Same thread, same refusal to turn creativity into a productivity game.",
  },
];

function Item({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#F4F1EC]/12">
      <button
        data-testid={`faq-question-${index + 1}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-display text-[22px] md:text-[26px] leading-snug text-[#F4F1EC] group-hover:text-[#FFB7C6] transition-colors">
          {q}
        </span>
        <span className="shrink-0 text-[#F4F1EC]/60 group-hover:text-[#FFB7C6] transition-colors">
          {open ? <Minus size={20} strokeWidth={1.5} /> : <Plus size={20} strokeWidth={1.5} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid={`faq-answer-${index + 1}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-7 pr-10 font-body text-[15px] leading-[1.8] text-[#F4F1EC]/70 max-w-2xl">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="wynn-faq"
      className="relative border-t border-[#F4F1EC]/10 bg-[#0A0910] scroll-mt-20"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
              Questions
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.05] text-[clamp(2rem,4vw,3rem)] font-medium text-[#F4F1EC]">
              A few quiet{" "}
              <span className="italic font-light text-aurora">answers</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            {FAQS.map((f, i) => (
              <Item key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
